import {
  modifyNewItem,
  getBlockChildrenDeep,
  addParentToBlocksSubflow,
  filterRecordOutOfTreeById,
  editNameOnTree,
  addRecordOnTree,
  moveRecordToParent,
} from "../../utils/BlockUtils/BlockUtils";
import {
  GET_BLOCK,
  GET_FAVORITE_BLOCKS,
  DELETE_BLOCK,
  UPDATE_BLOCKS_REQUEST,
  UPDATE_BLOCKS_ERROR,
  GET_BLOCKS_REQUEST,
  GET_BLOCKS_SUCCESS,
  GET_BLOCKS_ERROR,
  GET_BLOCKS_SUBFLOW_REQUEST,
  GET_BLOCKS_SUBFLOW_SUCCESS,
  GET_BLOCKS_SUBFLOW_ERROR,
  GET_FAVORITE_BLOCKS_REQUEST,
  GET_FAVORITE_BLOCKS_SUCCESS,
  GET_FAVORITE_BLOCKS_ERROR,
  MOVE_BLOCK_REQUEST,
  MOVE_BLOCK_SUCCESS,
  MOVE_BLOCK_ERROR,
  COPY_BLOCKS_REQUEST,
  COPY_BLOCKS_SUCCESS,
  CLEAR_BLOCK,
  CLEAR_BLOCKS,
  CLEAR_BLOCKS_WITH_SUBFLOW,
  GET_BLOCKS_DEPS_REQUEST,
  GET_BLOCKS_DEPS_SUCCESS,
  GET_BLOCKS_DEPS_ERROR,
  ADD_BLOCK_REQUEST,
  ADD_BLOCK_SUCCESS,
  ADD_BLOCK_ERROR,
  UPDATE_BLOCK_REQUEST,
  UPDATE_BLOCK_SUCCESS,
  UPDATE_BLOCK_ERROR,
  UPDATE_BLOCK_DATA_REQUEST,
  UPDATE_BLOCK_DATA_SUCCESS,
  UPDATE_BLOCK_DATA_ERROR,
  UPDATE_BLOCK_PROGRESS_REQUEST,
  UPDATE_BLOCK_PROGRESS_SUCCESS,
  UPDATE_BLOCK_PROGRESS_ERROR,
  UPDATE_BLOCKS_SUCCESS,
  CONVERT_BLOCK_ACTIVITY_TO_BLOCK,
  UPDATE_BLOCK_ACTIVITY,
  GET_GRID_BLOCKS_REQUEST,
  GET_GRID_BLOCKS_SUCCESS,
  DELETE_ACTIVITY_ON_MAPPING_GRID,
  DELETE_BLOCK_ON_MAPPING_GRID,
  UPDATE_NAME_ON_MAPPING_GRID,
  ADD_RECORD_ON_MAPPING_GRID,
  MOVE_RECORD_ON_MAPPING_GRID,
  PUSH_BLOCK_ONTO_MAPPING_SUBFLOW,
  START_BLOCK_STREAMING,
  STOP_BLOCK_STREAMING,
  UPDATE_BLOCK_PROPERTIES,
} from "../types";
import {
  updateItemInArray,
  mergeArrays,
} from "../../utils/FilterUtils/FilterUtils";

const initialState = {
  blocks: [],
  blocksSubflow: [],
  accumBlocks: [],
  accumBlocksSubflow: [],
  blocksWithDeps: [],
  favoriteBlocks: [],
  gridBlocks: [],
  block: {},
  isFetchingGrid: false,
  isFetching: false,
  isFetchingSubflow: false,
  isCopying: false,
  // True between START_BLOCK_STREAMING and STOP_BLOCK_STREAMING — GW's
  // "Generate scopes & structures" job is running and BLOCK_CREATED frames
  // are populating blocksSubflow one-by-one. While true, GET_BLOCKS_SUBFLOW
  // request/success are ignored so a late-mounting Mapping view doesn't
  // race the stream and repaint the canvas with the DB's current state.
  streamingBlocks: false,
};

// eslint-disable-next-line import/no-anonymous-default-export
export default function (state = initialState, { type, payload }) {
  switch (type) {
    case GET_BLOCK:
      return {
        ...state,
        block: payload,
      };
    case ADD_BLOCK_REQUEST:
      return {
        ...state,
        isFetching: true,
      };
    case ADD_BLOCK_SUCCESS:
      const newItemSubflow = modifyNewItem(payload.block, payload.blockType);

      return {
        ...state,
        blocks: [...state.blocks, payload.block],
        blocksSubflow: [...state.blocksSubflow, newItemSubflow],
        accumBlocks: payload.accumulate
          ? [...state.accumBlocks, payload.block]
          : [],
        accumBlocksSubflow: payload.accumulate
          ? [...state.accumBlocksSubflow, newItemSubflow]
          : [],
        isFetching: false,
      };
    case ADD_BLOCK_ERROR:
      return {
        ...state,
        error: payload,
        isFetching: false,
      };
    case UPDATE_BLOCK_REQUEST:
      return {
        ...state,
        isFetching: true,
      };
    case UPDATE_BLOCK_SUCCESS:
      return {
        ...state,
        blocks: updateItemInArray(state.blocks, payload),
        accumBlocks: updateItemInArray(state.accumBlocks, payload),
        isFetching: false,
      };
    case UPDATE_BLOCK_ERROR:
      return {
        ...state,
        error: payload,
      };
    case UPDATE_BLOCK_DATA_REQUEST:
      return state;
    case UPDATE_BLOCK_DATA_SUCCESS:
      return {
        ...state,
        blocks: updateItemInArray(state.blocks, payload),
        accumBlocks: updateItemInArray(state.accumBlocks, payload),
      };
    case UPDATE_BLOCK_DATA_ERROR:
      return {
        ...state,
        error: payload,
      };
    case UPDATE_BLOCK_PROGRESS_REQUEST:
      return state;
    case UPDATE_BLOCK_PROGRESS_SUCCESS:
      const children = getBlockChildrenDeep(state.blocks, payload.id);

      return {
        ...state,
        blocks: state.blocks.map((item) => {
          if (item.id === payload.id || children.includes(item.id)) {
            item.progress = payload.progress;
          }
          return item;
        }),
      };
    case UPDATE_BLOCK_PROGRESS_ERROR:
      return {
        ...state,
        error: payload,
      };
    case GET_BLOCKS_REQUEST:
      return {
        ...state,
        isFetching: true,
        blocks: [],
      };
    case GET_BLOCKS_SUCCESS:
      return {
        ...state,
        isFetching: false,
        blocks: payload.blocks,
        accumBlocks: payload.accumulate
          ? mergeArrays(state.accumBlocks, payload.blocks)
          : [],
      };
    case GET_BLOCKS_ERROR:
      return {
        ...state,
        isFetching: false,
        error: payload,
      };
    case GET_GRID_BLOCKS_REQUEST:
      return { ...state, isFetchingGrid: true, gridBlocks: [] };
    case GET_GRID_BLOCKS_SUCCESS:
      return { ...state, gridBlocks: payload, isFetchingGrid: false };
    case GET_BLOCKS_SUBFLOW_REQUEST:
      // While a GW scope-import is streaming (START_BLOCK_STREAMING dispatched),
      // preserve the streamed blocksSubflow — otherwise a late mount of
      // ScopeMappingView would wipe every block we've painted so far.
      if (state.streamingBlocks) return state;
      return {
        ...state,
        isFetchingSubflow: true,
        // An accumulating fetch (expanding one nested structure box inside an
        // already-rendered canvas) must keep the existing tree on screen —
        // wiping it here would empty `blocksSubflow` before the merge in
        // GET_BLOCKS_SUBFLOW_SUCCESS even runs, losing the ancestor nodes the
        // new blocks are supposed to nest under.
        blocksSubflow: payload?.accumulate ? state.blocksSubflow : [],
      };
    case GET_BLOCKS_SUBFLOW_SUCCESS: {
      // Same rationale as above: don't clobber the in-flight stream with the
      // DB snapshot from a mount-time fetch.
      if (state.streamingBlocks) return state;
      // An accumulating fetch (e.g. expanding a nested LIBRARY_STRUCTURE box
      // on the Generative Workspace Mapping canvas) must MERGE the newly
      // fetched subflow into the existing tree, stitching each root item
      // under `parentId` (the box just expanded) — mirroring what this
      // reducer already does correctly for `accumBlocksSubflow` (read by the
      // library Mapping pages). Previously `blocksSubflow` — the field
      // ScopeMappingView actually passes to ProcessMapping as `items` — was
      // unconditionally REPLACED regardless of `accumulate`, so expanding a
      // nested structure swapped the whole canvas for just that structure's
      // own blocks instead of nesting them into the existing tree.
      const stitchedBlocks = payload.accumulate
        ? addParentToBlocksSubflow(payload.blocks || [], payload.parentId)
        : payload.blocks || [];
      return {
        ...state,
        isFetchingSubflow: false,
        blocksSubflow: payload.accumulate
          ? mergeArrays(state.blocksSubflow, stitchedBlocks)
          : stitchedBlocks,
        accumBlocksSubflow: payload.accumulate
          ? mergeArrays(state.accumBlocksSubflow, stitchedBlocks)
          : [],
      };
    }
    case GET_BLOCKS_SUBFLOW_ERROR:
      return {
        ...state,
        isFetchingSubflow: false,
        error: payload,
      };
    case GET_BLOCKS_DEPS_REQUEST:
      return {
        ...state,
        isFetching: true,
        blocksWithDeps: [],
      };
    case GET_BLOCKS_DEPS_SUCCESS:
      return {
        ...state,
        isFetching: false,
        blocksWithDeps: payload,
      };
    case GET_BLOCKS_DEPS_ERROR:
      return {
        ...state,
        isFetching: false,
        error: payload,
      };
    case GET_FAVORITE_BLOCKS_REQUEST:
      return {
        ...state,
        isFetching: true,
        favoriteBlocks: [],
      };
    case GET_FAVORITE_BLOCKS_SUCCESS:
      return {
        ...state,
        isFetching: false,
        favoriteBlocks: payload,
      };
    case GET_FAVORITE_BLOCKS_ERROR:
      return {
        ...state,
        isFetching: false,
        error: payload,
      };
    case UPDATE_BLOCKS_REQUEST:
      return {
        ...state,
        isFetching: false,
        error: "",
      };
    case UPDATE_BLOCKS_SUCCESS:
      return {
        ...state,
        blocks: payload,
        isFetching: false,
      };
    case COPY_BLOCKS_REQUEST:
      return {
        ...state,
        //isCopying: true,
      };
    case COPY_BLOCKS_SUCCESS:
      /*const newBlockList = state.blocks;
      newBlockList.concat(payload);*/
      return {
        ...state,
        // isCopying: false,
      };
    case UPDATE_BLOCKS_ERROR:
      return {
        ...state,
        isCopying: false,
        error: payload,
      };
    case GET_FAVORITE_BLOCKS:
      return {
        ...state,
        favoriteBlocks: payload,
      };

    case DELETE_BLOCK:
      return {
        ...state,
        blocks: state.blocks.filter((block) => !payload.includes(+block.id)),
        blocksSubflow: state.blocksSubflow.filter(
          (block) => !payload.includes(+block.id)
        ),
        accumBlocks: state.accumBlocks.filter(
          (block) => !payload.includes(+block.id)
        ),
        accumBlocksSubflow: state.accumBlocksSubflow.filter(
          (block) => !payload.includes(+block.id)
        ),
      };
    case MOVE_BLOCK_REQUEST:
      return {
        ...state,
      };
    case MOVE_BLOCK_SUCCESS:
      const tmpMoveBlocks = state.blocks.map((item) => {
        if (item.id === payload.id) {
          return payload;
        }
        return item;
      });
      return {
        ...state,
        blocks: tmpMoveBlocks,
      };
    case MOVE_BLOCK_ERROR:
      return {
        ...state,
        error: payload,
      };
    case CONVERT_BLOCK_ACTIVITY_TO_BLOCK:
      return {
        ...state,
        blocks: state.blocks.map((block) => {
          if (block.id === payload.id) {
            block.activity = null;
          }
          return block;
        }),
        accumBlocks: state.accumBlocks.map((block) => {
          if (block.id === payload.id) {
            block.activity = null;
          }
          return block;
        }),
      };
    case UPDATE_BLOCK_ACTIVITY:
      return {
        ...state,
        blocks: state.blocks.map((block) => {
          if (block.activity?.id === payload.id) {
            block.activity = payload;
          }
          return block;
        }),
        accumBlocks: state.accumBlocks.map((block) => {
          if (block.activity?.id === payload.id) {
            block.activity = payload;
          }
          return block;
        }),
      };
    case CLEAR_BLOCKS:
      return {
        ...state,
        blocks: [],
      };
    case CLEAR_BLOCK:
      return {
        ...state,
        block: {},
      };
    case CLEAR_BLOCKS_WITH_SUBFLOW:
      return {
        ...state,
        blocksSubflow: [],
      };
    case DELETE_ACTIVITY_ON_MAPPING_GRID:
    case DELETE_BLOCK_ON_MAPPING_GRID:
      return {
        ...state,
        gridBlocks: filterRecordOutOfTreeById(state.gridBlocks, payload),
      };
    case UPDATE_NAME_ON_MAPPING_GRID:
      return {
        ...state,
        gridBlocks: editNameOnTree(state.gridBlocks, payload.id, payload.name),
      };
    case ADD_RECORD_ON_MAPPING_GRID:
      return {
        ...state,
        gridBlocks: addRecordOnTree(
          state.gridBlocks,
          payload.parentId,
          payload.record
        ),
      };
    case START_BLOCK_STREAMING:
      // Reset the canvas to empty so streaming frames build up from a
      // blank slate (the user asked to WATCH blocks appear, not to see
      // the existing scenario state under the incoming ones).
      return { ...state, blocksSubflow: [], streamingBlocks: true };
    case STOP_BLOCK_STREAMING:
      return { ...state, streamingBlocks: false };
    case PUSH_BLOCK_ONTO_MAPPING_SUBFLOW: {
      // Streamed one-at-a-time from BLOCK_CREATED WS frames while the GW
      // "Generate scopes & structures" job is producing blocks. Dedupe by
      // id so a late/re-sent frame (or a StrictMode-driven double dispatch)
      // doesn't paint the same node twice. Wire shape from
      // ScopeImportAsyncService#toBlockPayload is
      // {id, name, type, parentId, structureId, x, y, uxId, headScopeNumber}
      // — flatten to the ReactFlow node shape `modifyNewItem` produces so
      // ProcessMapping's layout / sortNodes can consume it without special
      // casing (they expect `data.label`, `parentNode` as String, etc.).
      if (!payload || payload.id == null) return state;
      const idStr = String(payload.id);
      if (state.blocksSubflow.some((b) => b && String(b.id) === idStr))
        return state;
      // GROUP blocks (BlockEnum.GROUP) become container nodes on the canvas;
      // HEAD / BLOCK / SCOPE all render as regular scenario blocks.
      // Hardcoded strings (not imported) because global/constants pulls in
      // i18n → Bryntum ESM which Jest's default transform can't handle from
      // this test's context. Values must stay in sync with `SCENARIO_GROUP`
      // / `SCENARIO_BLOCK` in src/global/constants.js.
      const isGroup = payload.type === "GROUP";
      // Defensive: BE payload SHOULD always carry structureId, but a stray
      // null would blow up modifyNewItem's `item.structure.id.toString()`.
      if (payload.structureId == null) return state;
      const node = modifyNewItem(
        {
          id: payload.id,
          name: payload.name,
          parentId: payload.parentId,
          // modifyNewItem expects `item.structure.id.toString()` — shim it.
          structure: { id: payload.structureId },
        },
        isGroup ? "scenario_group" : "scenario_block"
      );
      return {
        ...state,
        blocksSubflow: [...state.blocksSubflow, node],
      };
    }
    case MOVE_RECORD_ON_MAPPING_GRID:
      return {
        ...state,
        gridBlocks: moveRecordToParent(
          state.gridBlocks,
          payload.recordId,
          payload.parentId
        ),
        blocksSubflow: state.blocksSubflow.map((block) => {
          if (block.id == payload.recordId)
            return { ...block, parentNode: payload.parentId.toString() };
          else return block;
        }),
        blocks: state.blocks.map((block) => {
          if (block.id == payload.recordId)
            return { ...block, parentId: payload.parentId };
          else return block;
        }),
      };
    case UPDATE_BLOCK_PROPERTIES:
      const newBlocks = state.blocks.map(b => {
        if (b.id == payload.id) {
          b.bottleneck = payload.bottleneck;
          if (payload.bottleneck) {
            b.lagValue = payload.lagValue;
            b.linkType = payload.linkType;
          } else {
            b.lagValue = 0;
            b.linkType = "FS";
          }
          b.allowDuplication = payload.allowDuplication;
          b.constraintType = payload.constraintType;
          b.taskType = payload.taskType;
          b.inTimeline = payload.inTimeline;
          b.inRoadmap = payload.inRoadmap;
        }
        return b;
      })
      return {
        ...state,
        blocks: newBlocks,
      }
    default:
      return state;
  }
}
